/**
 * Wix Context
 *
 * Manages Wix app state including:
 * - Instance token and user identification
 * - User plan and credits from backend
 * - Trial status
 *
 * Used by WixDashboardPage and components rendered inside Wix.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  decodeWixInstance,
  getWixInstanceFromUrl,
  getWixAuthHeaders,
} from '../utils/wixInstance';
import type { WixInstanceData } from '../utils/wixInstance';
import { wixBiEventsService } from '../services/wixBiEvents';

const API_BASE = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

interface WixUserData {
  userId: string;
  plan: string;
  credits: number;
  creditsUsed: number;
  billingSource: 'wix' | 'stripe';
  isTrial: boolean;
  trialCreditsRemaining: number;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface WixContextType {
  // Instance data
  instanceToken: string | null;
  instanceData: WixInstanceData | null;

  // User data
  userData: WixUserData | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  refreshUserData: () => Promise<void>;
  useTrialCredit: () => Promise<boolean>;
  hasCredits: boolean;
  canUseTrial: boolean;

  // BI Events tracking
  trackBiEvent: (eventName: string, params?: Record<string, unknown>) => void;
}

const WixContext = createContext<WixContextType | null>(null);

export function WixProvider({ children }: { children: React.ReactNode }) {
  const [instanceToken, setInstanceToken] = useState<string | null>(null);
  const [instanceData, setInstanceData] = useState<WixInstanceData | null>(null);
  const [userData, setUserData] = useState<WixUserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Initialize from URL on mount
  useEffect(() => {
    const token = getWixInstanceFromUrl();
    if (token) {
      setInstanceToken(token);
      const decoded = decodeWixInstance(token);
      setInstanceData(decoded);

      // Initialize BI Events service with instance token
      wixBiEventsService.initialize(token);

      // Check for upgrade=success parameter (user returning from checkout)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('upgrade') === 'success') {
        console.log('Upgrade success detected, will refresh user data');
        // Remove the parameter from URL to prevent refresh loop
        urlParams.delete('upgrade');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
        // Trigger user data refresh
        setRefreshTrigger(prev => prev + 1);
      }
    } else {
      setIsLoading(false);
      setError('No Wix instance token found');
    }
  }, []);

  // Fetch user data when instance is available
  const fetchUserData = useCallback(async () => {
    if (!instanceToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/v1/wix/user/status`, {
        method: 'GET',
        headers: getWixAuthHeaders(instanceToken),
      });

      if (!response.ok) {
        if (response.status === 404) {
          // New user - they'll be created on first action
          setUserData({
            userId: '',
            plan: 'free',
            credits: 0,
            creditsUsed: 0,
            billingSource: 'wix',
            isTrial: true,
            trialCreditsRemaining: 1, // One free trial generation
            currentPeriodEnd: null,
            cancelAtPeriodEnd: false,
          });
          return;
        }
        throw new Error('Failed to fetch user data');
      }

      const data = await response.json();
      setUserData({
        userId: data.user_id || '',
        plan: data.plan || 'free',
        credits: data.credits || 0,
        creditsUsed: data.credits_used || 0,
        billingSource: data.billing_source || 'wix',
        isTrial: data.is_trial ?? (data.plan === 'free'),
        trialCreditsRemaining: data.trial_credits_remaining ?? 1,
        currentPeriodEnd: data.current_period_end,
        cancelAtPeriodEnd: data.cancel_at_period_end || false,
      });
    } catch (err) {
      console.error('Error fetching Wix user data:', err);
      // Set default trial user on error
      setUserData({
        userId: '',
        plan: 'free',
        credits: 0,
        creditsUsed: 0,
        billingSource: 'wix',
        isTrial: true,
        trialCreditsRemaining: 1,
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [instanceToken]);

  useEffect(() => {
    if (instanceToken) {
      fetchUserData();
    }
  }, [instanceToken, fetchUserData, refreshTrigger]);

  // Use a trial credit for one-time demo
  const useTrialCredit = useCallback(async (): Promise<boolean> => {
    if (!instanceToken || !userData?.trialCreditsRemaining) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/wix/user/use-trial`, {
        method: 'POST',
        headers: getWixAuthHeaders(instanceToken),
      });

      if (response.ok) {
        setUserData(prev => prev ? {
          ...prev,
          trialCreditsRemaining: Math.max(0, prev.trialCreditsRemaining - 1),
        } : null);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [instanceToken, userData]);

  const hasCredits = (userData?.credits ?? 0) > 0;
  const canUseTrial = (userData?.trialCreditsRemaining ?? 0) > 0 && !hasCredits;

  // BI Events tracking wrapper
  const trackBiEvent = useCallback((eventName: string, params?: Record<string, unknown>) => {
    wixBiEventsService.trackEvent(eventName, params);
  }, []);

  const value: WixContextType = {
    instanceToken,
    instanceData,
    userData,
    isLoading,
    error,
    refreshUserData: fetchUserData,
    useTrialCredit,
    hasCredits,
    canUseTrial,
    trackBiEvent,
  };

  return <WixContext.Provider value={value}>{children}</WixContext.Provider>;
}

export function useWix() {
  const context = useContext(WixContext);
  if (!context) {
    throw new Error('useWix must be used within a WixProvider');
  }
  return context;
}
